import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AuthStatus } from '@hotmart/space-lib/dist/enum'
import Button from '@hotmart/space-lib/dist/components/button'
import { useUser } from 'src/hook/useUser'
import { Language } from 'src/utils/constants'
import { Loading, PostCommentInput } from 'src/components'
import i18n, { i18nInit } from 'src/i18n'
import { SurvicateUtils } from 'src/utils/survicate'
import { useDecision } from '@optimizely/react-sdk'
import { MAX_COMMENTS_PER_PAGES } from './postComments.constants'
import { CommentType, PathParams, PostCommentsProps } from './postComments.types'
import { PostCommentItem } from './components'
import { usePostComments } from './hooks'
import { CommentInputBlockState } from './components/commentInputBlockState'

const PostComments = ({
  post,
  ownerCollectionId,
  canUserComment = true,
  isCommentsEnabled,
  authorName,
  authorRole
}: PostCommentsProps) => {
  const [{ enabled: enableEditBadge }] = useDecision('com_eng_web_enable_edit_comment')
  const { lang, idPost } = useParams() as PathParams
  const { authStatus, user, isOwnerOrAdminOrModerator } = useUser()
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    usePostComments({
      postId: Number(idPost),
      commentsPerPage: MAX_COMMENTS_PER_PAGES
    })

  const comments = useMemo(() => {
    return data?.pages.flatMap((page) => page.data)
  }, [data])

  const totalComments = useMemo(() => {
    const commentsLength = comments?.length || 0
    const repliesLength = comments?.flatMap((comment) => comment?.replies || []).length || 0

    return commentsLength + repliesLength
  }, [comments])

  const handleLoadMoreComments = () => {
    const pages = data?.pages
    const lastPage = pages?.[pages.length - 1]
    const nextPageToken = lastPage?.nextPageToken

    fetchNextPage({ pageParam: nextPageToken })
  }

  const handleRefetchComments = () => {
    refetch()
  }

  const hasValidComments = Array.isArray(comments) && comments?.length > 0
  const showComments = hasValidComments && !isLoading
  const allowMedias = [CommentType.IMAGE, CommentType.DOCUMENT]
  const showCommentInputNew = canUserComment && (isCommentsEnabled || isOwnerOrAdminOrModerator)
  const shouldShowCommentInput = showCommentInputNew

  useEffect(() => {
    i18nInit(Language[lang.toUpperCase()])

    if (user?.id) {
      SurvicateUtils.initSurvicate(user.id)
    }
  }, [user, lang])

  return (
    <section className="_mt-6">
      <div className={'_d-flex _align-items-center _mb-4 _justify-content-start'}>
        <p className="_text-gray-900 _text-2 _font-weight-bold _m-0">
          {i18n.t('comments.comments')}{' '}
          <span className="_text-gray-500 _font-weight" data-test="comments-count">
            {totalComments}
          </span>
        </p>
      </div>

      {!shouldShowCommentInput && <CommentInputBlockState />}

      {shouldShowCommentInput && (
        <PostCommentInput
          idPost={Number(idPost)}
          allowMedias={allowMedias}
          authorRole={post?.authorMemberType}
          authorName={authorName || post?.authorName}
          authorUcode={post?.authorUcode}
          enableMentions={authStatus === AuthStatus.AUTHENTICATED}
          enableBreakLine
          commentSuggestion={authStatus === AuthStatus.AUTHENTICATED}
          enableAICommentSuggestion={authStatus === AuthStatus.AUTHENTICATED}
          onSuccess={handleRefetchComments}
        />
      )}

      <div className="_w-full _pr-2">
        {isLoading && <Loading full />}
        {showComments &&
          comments.map((comment) => (
            <PostCommentItem
              key={comment.id}
              postId={Number(idPost)}
              ownerCollectionId={ownerCollectionId}
              comment={comment}
              authorRole={authorRole}
              onReloadComments={handleRefetchComments}
              canUserRespondToComment={isCommentsEnabled || isOwnerOrAdminOrModerator}
              enableEditBadge={enableEditBadge}
            />
          ))}
      </div>

      {hasNextPage && (
        <div className="_d-flex _justify-content-center  _border-top _border-gray-200">
          <Button
            variation="primary"
            className="_mt-5"
            loading={isFetchingNextPage}
            disabled={isFetchingNextPage}
            onClick={handleLoadMoreComments}
          >
            {i18n.t('comments.show_more_answers')}
          </Button>
        </div>
      )}
    </section>
  )
}

export default PostComments
